import { z } from "zod";
import {
	defineMotionKind,
	MOTION_AT_REST,
	motionProgress,
} from "../define-motion-kind";
import { ease, lerp } from "../easing";

const emptyParamsSchema = z.object({}).strict();

const directionParamsSchema = z
	.object({ from: z.enum(["left", "right", "up", "down"]) })
	.strict();

type TravelDirection = z.infer<typeof directionParamsSchema>["from"];

/** Offset (percent of the element) the element starts from / leaves to. */
function travel(from: TravelDirection, distance: number): string {
	switch (from) {
		case "left":
			return `translateX(${-distance}%)`;
		case "right":
			return `translateX(${distance}%)`;
		case "up":
			return `translateY(${-distance}%)`;
		case "down":
			return `translateY(${distance}%)`;
	}
}

/** Card-to-card transitions (deck card in/out, video beat joins). */
export const cutTransition = defineMotionKind({
	key: "cut",
	role: "transition",
	label: "Cut",
	paramsSchema: emptyParamsSchema,
	defaultParams: {},
	defaultDurationMs: 0,
	gestureCapable: true,
	reducedMotionFallback: "none",
	render: () => MOTION_AT_REST,
});

/** Linear opacity, identical to the pre-registry deck FADE. */
export const fadeTransition = defineMotionKind({
	key: "fade",
	role: "transition",
	label: "Fade",
	paramsSchema: emptyParamsSchema,
	defaultParams: {},
	defaultDurationMs: 300,
	gestureCapable: true,
	reducedMotionFallback: "opacity",
	render: (input) => ({ opacity: motionProgress(input), reveal: 1 }),
});

/** Paired fade: both cards are visible while one fades in over the other. */
export const crossfadeTransition = defineMotionKind({
	key: "crossfade",
	role: "transition",
	label: "Crossfade",
	paramsSchema: emptyParamsSchema,
	defaultParams: {},
	defaultDurationMs: 400,
	gestureCapable: true,
	reducedMotionFallback: "opacity",
	render: (input) => ({
		opacity: ease(motionProgress(input), "standard"),
		reveal: 1,
	}),
});

export const slideTransition = defineMotionKind({
	key: "slide",
	role: "transition",
	label: "Slide",
	paramsSchema: directionParamsSchema,
	defaultParams: { from: "right" },
	defaultDurationMs: 350,
	gestureCapable: true,
	reducedMotionFallback: "opacity",
	render: (input) => {
		const amount = ease(
			motionProgress(input),
			input.direction === "in" ? "enter" : "exit",
		);
		return {
			opacity: amount,
			transform: travel(input.params.from, lerp(24, 0, amount)),
			reveal: 1,
		};
	},
});

/** The incoming card pushes the outgoing one off: full travel, no fade. */
export const pushTransition = defineMotionKind({
	key: "push",
	role: "transition",
	label: "Push",
	paramsSchema: directionParamsSchema,
	defaultParams: { from: "right" },
	defaultDurationMs: 400,
	gestureCapable: true,
	reducedMotionFallback: "opacity",
	render: (input) => {
		const amount = ease(motionProgress(input), "standard");
		return {
			opacity: 1,
			transform: travel(input.params.from, lerp(100, 0, amount)),
			reveal: 1,
		};
	},
});

export const wipeTransition = defineMotionKind({
	key: "wipe",
	role: "transition",
	label: "Wipe",
	paramsSchema: directionParamsSchema,
	defaultParams: { from: "left" },
	defaultDurationMs: 450,
	gestureCapable: false,
	reducedMotionFallback: "opacity",
	render: (input) => {
		const hidden = (1 - ease(motionProgress(input), "standard")) * 100;
		const inset: Record<TravelDirection, string> = {
			left: `inset(0 ${hidden}% 0 0)`,
			right: `inset(0 0 0 ${hidden}%)`,
			up: `inset(0 0 ${hidden}% 0)`,
			down: `inset(${hidden}% 0 0 0)`,
		};
		return { opacity: 1, clipPath: inset[input.params.from], reveal: 1 };
	},
});

export const zoomTransition = defineMotionKind({
	key: "zoom",
	role: "transition",
	label: "Zoom",
	paramsSchema: z
		.object({ fromScale: z.number().min(0.5).max(1.5) })
		.strict(),
	defaultParams: { fromScale: 0.9 },
	defaultDurationMs: 350,
	gestureCapable: false,
	reducedMotionFallback: "opacity",
	render: (input) => {
		const amount = ease(
			motionProgress(input),
			input.direction === "in" ? "enter" : "exit",
		);
		return {
			opacity: amount,
			transform: `scale(${lerp(input.params.fromScale, 1, amount)})`,
			reveal: 1,
		};
	},
});

export const blurTransition = defineMotionKind({
	key: "blur",
	role: "transition",
	label: "Blur",
	paramsSchema: z.object({ radiusPx: z.number().min(0).max(40) }).strict(),
	defaultParams: { radiusPx: 12 },
	defaultDurationMs: 400,
	gestureCapable: false,
	reducedMotionFallback: "opacity",
	render: (input) => {
		const amount = ease(motionProgress(input), "standard");
		return {
			opacity: amount,
			filter: `blur(${lerp(input.params.radiusPx, 0, amount)}px)`,
			reveal: 1,
		};
	},
});

export const TRANSITION_KINDS = [
	cutTransition,
	fadeTransition,
	crossfadeTransition,
	slideTransition,
	pushTransition,
	wipeTransition,
	zoomTransition,
	blurTransition,
] as const;
